import React, { useCallback, useEffect, useState } from "react";
import DebouncedInput from "../../components/debounced-input/debounced-input";
import { GET_TRANSACTIONS_QUERY } from "../../graphql/queries/transactions";
import { useLazyQuery } from "@apollo/client";
import TableCellRenderer from "../../components/table/table-cell-renderer";
import Table from "../../components/table/table";
import Loader from "../../components/loader/loader";
import { formatTimestamp } from "../../utils/date-converter";
import Modal from "../../components/modal/Modal";
import TransactionFilters from "../../components/transaction-filters/transaction-filters";
import Button from "../../components/Button/Button";
import hasValue from "../../utils/hasValue";
import { SKIP, FIRST } from "../../utils/pagination";
import stringifyArray from "../../utils/arrayToString";

const RecentTransactions = () => {
  const [filtersModal, setFiltersModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [searchValue, setSearchValue] = useState(undefined);
  const [paginationInfo, setPaginationInfo] = useState({
    hasNextPage: undefined,
    hasPreviousPage: undefined,
    totalPages: undefined,
    totalRecords: undefined,
    currentPage: 1,
  });
  const [sortField, setSortField] = useState("timestamp"); // default sort field
  const [sortOrder, setSortOrder] = useState("desc"); // default sort order

  const handleInputChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const generateFilterValues = (data) => {
    if (data?.length > 0) {
      return data?.map((d) => d?.value);
    }

    return undefined;
  };

  const [fetchRecentTransactions, { data, loading, error }] = useLazyQuery(
    GET_TRANSACTIONS_QUERY,
    {
      variables: {
        search: searchValue,
        first: FIRST,
        skip: (paginationInfo.currentPage - 1) * SKIP,
        success:
          appliedFilters?.success !== undefined &&
          appliedFilters?.success?.length > 0
            ? appliedFilters.success[0].value === "Yes"
            : undefined,
        platform: generateFilterValues(appliedFilters.platform),
        vendor: generateFilterValues(appliedFilters.vendor),
        purchaseType: generateFilterValues(appliedFilters.purchaseType),
        grid: generateFilterValues(appliedFilters.grid),
        sortField: sortField,
        sortOrder: sortOrder,
      },
    }
  );

  useEffect(() => {
    fetchRecentTransactions();
  }, [searchValue, sortField, sortOrder]);

  const onFilter = () => {
    setFiltersModal((prev) => !prev);
  };

  const onApplyFilters = () => {};

  useEffect(() => {
    if (hasValue(appliedFilters)) {
      fetchRecentTransactions();
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (data) {
      setPaginationInfo((prev) => ({
        ...prev,
        currentPage: data.transactions.currentPage,
        hasNextPage: data.transactions.hasNextPage,
        hasPreviousPage: data.transactions.hasPreviousPage,
        totalPages: data.transactions.totalPages,
        totalRecords: data.transactions.totalRecords,
      }));
    }
  }, [data]);

  const onNextPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage + 1,
    }));
  };

  const onPrevPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage - 1,
    }));
  };

  const onClearFilters = () => {
    setAppliedFilters({});
    fetchRecentTransactions();
  };

  const displayFilters = (appliedFilters) => {
    const filterStrings = [];

    if (appliedFilters?.success?.length > 0) {
      filterStrings.push(`Success: ${stringifyArray(appliedFilters.success)}`);
    }

    if (appliedFilters?.platform?.length > 0) {
      filterStrings.push(
        `Transaction Platform: ${stringifyArray(appliedFilters.platform)}`
      );
    }

    if (appliedFilters?.vendor?.length > 0) {
      filterStrings.push(`Vendor: ${stringifyArray(appliedFilters.vendor)}`);
    }

    if (appliedFilters?.purchaseType?.length > 0) {
      filterStrings.push(
        `Purchase Type: ${stringifyArray(appliedFilters.purchaseType)}`
      );
    }

    if (appliedFilters?.grid?.length > 0) {
      filterStrings.push(`Grid: ${stringifyArray(appliedFilters.grid)}`);
    }

    return (
      <div className="basic-modal__sub-header-text">
        {filterStrings.join(", ")}
      </div>
    );
  };

  const handleSortChange = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1, // Reset pagination when sort changes
    }));
  };

  if (error) {
    return <div className="display-page__error">Something went wrong</div>;
  }

  return (
    <div className="display-page">
      <Modal modal={filtersModal} onClose={onFilter} modalSize="large">
        <>
          <TransactionFilters
            onFilter={onFilter}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            onApplyFilters={onApplyFilters}
          />
        </>
      </Modal>
      <div className="display-page__header">
        <div className="display-page__main-header">
          <div className="display-page__search-input">
            <img
              src="/images/search.svg"
              alt="search-icon"
              className="display-page__search-icon"
            />
            <DebouncedInput
              className="input-group input-group__search"
              debounceTimeout={400}
              minLength={3}
              onChange={(e) => {
                handleInputChange(e);
              }}
            />
          </div>
          <Button
            onClick={onFilter}
            name={"Filter"}
            className={"button--primary"}
          />
        </div>
      </div>
      <div className="display-page__sub-header">
        {hasValue(appliedFilters) && (
          <div className="display-page__filters">
            <div className="display-page__sub-header-text">
              Applied Filters: {displayFilters(appliedFilters)}
            </div>
            <div
              onClick={onClearFilters}
              className="display-page__filters-clear"
            >
              Clear Filters
            </div>
          </div>
        )}
      </div>
      <div className="display-page__info-wrapper">
        Displaying{" "}
        <span className="display-page__info--bold">
          {paginationInfo.currentPage === 1
            ? 1
            : (paginationInfo.currentPage - 1) * SKIP + 1}{" "}
        </span>
        to{" "}
        <span className="display-page__info--bold">
          {paginationInfo.currentPage && !loading
            ? Math.min(
                paginationInfo.currentPage * SKIP,
                paginationInfo.totalRecords
              )
            : "-"}
        </span>{" "}
        of{" "}
        <span className="display-page__info--bold">
          {paginationInfo.totalRecords}
        </span>{" "}
        total.
      </div>
      <div className="details-page__table">
        {loading ? (
          <Loader sizeClass={"size--medium"} />
        ) : (
          <Table
            data={data?.transactions?.items}
            columns={[
              {
                field: "timestamp",
                title: "Date",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ timestamp }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${formatTimestamp(timestamp).date} ${
                        formatTimestamp(timestamp).time
                      }`}
                    />
                  );
                },
                sortable: true,
              },
              {
                field: "clientID",
                title: "Client ID",
                showOnMobile: true,
                hideMobileUp: false,
                textAlign: "center",
                className: "table-wrapper__table__cell--narrow-client-id",
                render: ({ client }) => {
                  return <TableCellRenderer firstItem={client?.clientId} />;
                },
              },
              {
                field: "grid",
                title: "Grid",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ grid }) => {
                  return <TableCellRenderer firstItem={grid || "--"} />;
                },
              },
              {
                field: "amount",
                title: "Amount",
                showOnMobile: true,
                hideMobileUp: false,
                textAlign: "right",
                className: "table-wrapper__table__cell--narrow-amount",
                render: ({ amount }) => {
                  return <TableCellRenderer firstItem={amount.toFixed(2)} />;
                },
                sortable: true,
              },
              {
                field: "success",
                title: "Success",
                showOnMobile: true,
                hideMobileUp: false,
                textAlign: "center",
                className: "table-wrapper__table__cell--narrow-success",
                render: ({ success }) => {
                  return (
                    <TableCellRenderer firstItem={success ? "Yes" : "No"} />
                  );
                },
              },
              {
                field: "purchaseType",
                title: "Purchase Type",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ purchaseType }) => {
                  return <TableCellRenderer firstItem={purchaseType} />;
                },
              },
              {
                field: "vendor",
                title: "Vendor",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ vendor }) => {
                  return <TableCellRenderer firstItem={vendor} />;
                },
              },
              {
                field: "voucherPin",
                title: "Voucher Pin",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ voucherPin }) => {
                  return <TableCellRenderer firstItem={voucherPin || "--"} />;
                },
              },
              {
                field: "platform",
                title: "Platform",
                showOnMobile: true,
                hideMobileUp: false,
                textAlign: "center",
                className: "table-wrapper__table__cell--narrow-platform",
                render: ({ platform }) => {
                  return <TableCellRenderer firstItem={platform} />;
                },
              },
              {
                field: "errors",
                title: "Errors",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ errors }) => {
                  return (
                    <TableCellRenderer
                      firstItem={errors || "--"}
                      firstItemClass={errors ? "danger" : ""}
                    />
                  );
                },
              },
              {
                field: "transactionId",
                title: "Transaction ID",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ transactionId }) => {
                  return <TableCellRenderer firstItem={transactionId} />;
                },
              },
            ]}
            paged
            hasNextPage={paginationInfo.hasNextPage}
            hasPrevPage={paginationInfo.hasPreviousPage}
            onNextPage={onNextPageClick}
            onPrevPage={onPrevPageClick}
            sortConfig={{ key: sortField, direction: sortOrder }}
            onSortChange={handleSortChange}
          />
        )}
      </div>
    </div>
  );
};

export default RecentTransactions;
